import PressureIntervalInput from '@/components/PressureIntervalInput';
import {intervalMs} from '@/lib/pressure-interval.mjs';
import {usePressureText} from '@/lib/pressure-text';

import { useEffect, useState, useCallback } from 'react';

import { Button } from '@/components/ui/button';

import { Input } from '@/components/ui/input';

import { Label } from '@/components/ui/label';

import { Textarea } from '@/components/ui/textarea';

import { useAuth } from '@/context/AuthContext';

import { toast } from 'sonner';

import { pget, pwrite, pressureError, newConfig, FORMULAS } from '@/lib/pressure';

export const selectClass = 'h-11 w-full rounded-md border border-input bg-background px-3 text-sm';

export function Field({

  label,

  children

}) {

  return <label className="flex min-w-0 flex-col gap-2 text-sm"><span className="font-medium">{label}</span>{children}</label>;

}

const box = 'rounded-xl border border-border bg-card p-4 sm:p-6';

export function SensorLibrary({

  organizationId,

  onSaved

}) {

  const {

    pt,

    locale

  } = usePressureText();

  const {

      user

    } = useAuth(),

    editable = ['admin', 'manager'].includes(user?.role);

  const [profiles, setProfiles] = useState([]),

    [revision, setRevision] = useState(''),

    [form, setForm] = useState({

      manufacturer: '',

      model: '',

      signal_type: '4-20mA',

      engineering_min: '',

      engineering_max: '',

      default_sample_rate_hz: 20,

      notes: ''

    });

  const [busy, setBusy] = useState(false);

  const load = useCallback(() => pget('/sensor-profiles', organizationId).then(r => setProfiles(r.items)).catch(e => toast.error(pressureError(e))), [organizationId]);

  useEffect(() => {

    load();

  }, [load]);

  const field = (key, val) => setForm(f => ({

    ...f,

    [key]: val

  }));

  const save = async e => {

    e.preventDefault();

    setBusy(true);

    try {

      await pwrite('/sensor-profiles' + (revision ? `/${revision}/revisions` : ''), 'POST', {

        ...form,

        raw_min: form.signal_type === '0.5-4.5V' ? 0.5 : 4,

        raw_max: form.signal_type === '0.5-4.5V' ? 4.5 : 20,

        raw_unit: form.signal_type === '0.5-4.5V' ? 'V' : 'mA',

        unit: 'bar'

      }, organizationId);

      await load();

      onSaved?.();

      toast.success(pt("Sensorrevisie opgeslagen. Eerdere metingen blijven gelijk."));

    } catch (e) {

      toast.error(pressureError(e));

    } finally {

      setBusy(false);

    }

  };

  return <div className="space-y-5"><div className={box}><h2 className="text-xl font-bold">{pt("Sensorbibliotheek")}</h2><p className="mt-1 text-sm text-muted-foreground">{pt("Herbruikbare 4\u201320 mA- en 0,5\u20134,5 V-sensoren. Vul het drukbereik in volgens het sensortypeplaatje. Elke wijziging maakt een nieuwe revisie.")}</p><div className="mt-4 grid gap-3 sm:grid-cols-2">{profiles.map(p => <div key={p.id + p.revision} className="rounded-lg border p-4"><b>{p.manufacturer} · {p.model}</b><div className="mt-1 text-sm text-muted-foreground">{p.raw_min}–{p.raw_max} {p.raw_unit} → {p.engineering_min}–{p.engineering_max} {pt("bar \xB7 revisie")} {p.revision} · {intervalMs(p.default_sample_rate_hz)} ms</div><p className="mt-2 text-sm">{p.notes}</p>{editable && <Button className="mt-3" variant="outline" size="sm" onClick={() => {

            setRevision(p.id);

            const {

              id,

              revision,

              ...data

            } = p;

            setForm(data);

          }}>{pt("Nieuwe revisie")}</Button>}</div>)}{!profiles.length && <p className="text-sm">{pt("Nog geen sensorprofielen. Voeg hieronder de eerste sensor toe.")}</p>}</div></div>{editable && <form onSubmit={save} className={box}><div className="flex items-center justify-between"><h3 className="font-bold">{revision ? pt("Nieuwe profielrevisie") : pt("Sensor toevoegen")}</h3>{revision && <Button type="button" variant="ghost" onClick={() => setRevision('')}>{pt("Nieuw profiel")}</Button>}</div><div className="mt-4 grid gap-4 sm:grid-cols-2"><Field label={pt("Elektrisch sensorsignaal")}><select className={selectClass} value={form.signal_type} onChange={e => field('signal_type', e.target.value)}><option value="4-20mA">{pt("4\u201320 mA stroom")}</option><option value="0.5-4.5V">{pt("0,5\u20134,5 V spanning")}</option></select></Field><Field label={pt("Fabrikant")}><Input required value={form.manufacturer} onChange={e => field('manufacturer', e.target.value)} /></Field><Field label={pt("Model / artikelnummer")}><Input required value={form.model} onChange={e => field('model', e.target.value)} /></Field>{[['engineering_min', 'Minimum (bar)'], ['engineering_max', 'Maximum (bar)'], ['default_sample_rate_hz', pt("Standaardmeetinterval (ms)")]].map(([k, l]) => <Field key={k} label={pt(l)}><>{k==='default_sample_rate_hz'?<PressureIntervalInput value={form[k]} maxHz={100} onChange={hz=>field(k,hz)}/>:<Input type="number" required step="any" value={form[k]} onChange={e => field(k, e.target.value === '' ? '' : Number(e.target.value))} />}</></Field>)}<Field label={pt("Notities")}><Textarea value={form.notes} onChange={e => field('notes', e.target.value)} /></Field></div><Button className="mt-4" disabled={busy}>{pt("Sensorrevisie bewaren")}</Button></form>}</div>;

}

export function ChannelEditor({

  device,

  config,

  onSaved,

  reloadProfiles = 0,

  guided = false

}) {

  const {

    pt,

    locale

  } = usePressureText();

  const [manage, setManage] = useState(false),

    [profileTick, setProfileTick] = useState(0);

  const [draft, setDraft] = useState(config || newConfig(device.id)),

    [profiles, setProfiles] = useState([]),

    [busy, setBusy] = useState(false);

  useEffect(() => setDraft(config || newConfig(device.id)), [config, device.id]);

  useEffect(() => {

    pget('/sensor-profiles', device.organization_id).then(r => setProfiles(r.items)).catch(e => toast.error(pressureError(e)));

  }, [device.organization_id, reloadProfiles, profileTick]);

  const update = (i, key, value) => setDraft(c => ({

    ...c,

    channels: c.channels.map((ch, n) => n === i ? {

      ...ch,

      [key]: value

    } : ch)

  }));

  const add = () => {

    if (draft.channels.length === 4) return;

    const id = [1, 2, 3, 4].map(n => 'CH' + n).find(id => !draft.channels.some(c => c.channel_id === id));

    const p = profiles.at(-1);

    if (!p) {

      setManage(true);

      toast.info(pt("Maak eerst een sensorprofiel aan."));

      return;

    }

    const {

      id: pid,

      revision,

      ...snapshot

    } = p;

    setDraft(c => ({

      ...c,

      channels: [...c.channels, {

        channel_id: id,

        name: pt("Druk ") + id,

        input_id: guided ? 'AIO_INPUT_1' : '',

        sensor_profile_id: pid,

        sensor_profile_revision: revision,

        sensor_profile: snapshot,

        offset_bar: 0,

        working_pressure_bar: ''

      }]

    }));

  };

  const save = async e => {

    e.preventDefault();

    setBusy(true);

    try {

      const result = await pwrite(`/devices/${device.id}/config`, 'PUT', {

        ...draft,

        ...(guided ? {sample_rate_hz:Math.min(draft.sample_rate_hz||1,device.max_sample_rate_hz||1),channels:draft.channels.map((ch,i)=>({...ch,input_id:ch.input_id||'AIO_INPUT_'+(i+1)}))} : {}),

        config_version: (config?.config_version || 0) + 1

      });

      onSaved(result);

      toast.success(pt("Configuratie opgeslagen; de logger bevestigt toepassing bij synchronisatie."));

    } catch (e) {

      toast.error(pressureError(e));

    } finally {

      setBusy(false);

    }

  };

  return <div className="space-y-3"><form onSubmit={save} className="space-y-5"><div className={box}><div className="flex flex-wrap justify-between gap-3"><div><h2 className="text-xl font-bold">{pt("Fysieke drukkanalen")}</h2><p className="mt-1 text-sm text-muted-foreground">{guided?pt("De automatische AIO-pilotcontrole ondersteunt één sensor. Kies het sensorprofiel en de fysieke ingang; de technische naam wordt later automatisch uitgelezen."):pt("Koppel 1\u20134 sensoren aan de werkelijke analoge ingangen.")}</p></div><Button type="button" variant="outline" disabled={draft.channels.length >= (guided?1:4)} onClick={add}>{pt("Kanaal toevoegen")}</Button></div><div className="mt-5 space-y-4">{draft.channels.map((ch, i) => <div key={ch.channel_id} className="rounded-lg border border-border bg-muted/20 p-4"><div className="mb-4 flex items-center justify-between"><b>{ch.channel_id}</b><Button type="button" variant="ghost" size="sm" onClick={() => setDraft(c => ({

                ...c,

                channels: c.channels.filter(x => x.channel_id !== ch.channel_id)

              }))}>{pt("Verwijderen")}</Button></div><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"><Field label={pt("Kanaalnaam")}><Input required value={ch.name} onChange={e => update(i, 'name', e.target.value)} /></Field>{guided?<Field label={pt("AIO-ingang")}><select className={selectClass} value={ch.input_id||'AIO_INPUT_1'} onChange={e=>update(i,'input_id',e.target.value)}>{ch.input_id&&!/^AIO_INPUT_[1-4]$/.test(ch.input_id)&&<option value={ch.input_id}>{pt("Bestaande ingang · wordt later uitgelezen")}</option>}{[1,2,3,4].map(n=><option key={n} value={'AIO_INPUT_'+n}>{pt("Ingang")} {n}</option>)}</select></Field>:<Field label={pt("Fysieke input-ID")}><Input required value={ch.input_id} placeholder={pt("Naam uit geverifieerde inputmapping")} onChange={e => update(i, 'input_id', e.target.value)} /></Field>}<Field label={pt("Sensor en revisie")}><select className={selectClass} value={`${ch.sensor_profile_id}/${ch.sensor_profile_revision}`} onChange={e => {

                  const p = profiles.find(p => `${p.id}/${p.revision}` === e.target.value);

                  const {

                    id,

                    revision,

                    ...snapshot

                  } = p;

                  setDraft(c => ({

                    ...c,

                    channels: c.channels.map((x, n) => n === i ? {

                      ...x,

                      sensor_profile_id: id,

                      sensor_profile_revision: revision,

                      sensor_profile: snapshot

                    } : x)

                  }));

                }}>{profiles.map(p => <option key={p.id + p.revision} value={`${p.id}/${p.revision}`}>{p.manufacturer} {p.model} · r{p.revision} · {p.engineering_max} bar</option>)}</select></Field><Field label={pt("Werkdruk (bar)")}><Input type="number" step="any" value={ch.working_pressure_bar} onChange={e => update(i, 'working_pressure_bar', Number(e.target.value))} /></Field><Field label="Offset (bar)"><Input type="number" step="any" value={ch.offset_bar} onChange={e => update(i, 'offset_bar', Number(e.target.value))} /></Field><p className="self-end text-sm text-muted-foreground">{ch.sensor_profile.raw_min}–{ch.sensor_profile.raw_max} {ch.sensor_profile.raw_unit} → {ch.sensor_profile.engineering_min}–{ch.sensor_profile.engineering_max} bar</p></div></div>)}</div></div><div className={box}><h3 className="font-bold">{pt("Acquisitie en capture")}</h3><div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"><Field label={pt("Meetinterval (ms)")}><PressureIntervalInput value={draft.sample_rate_hz} maxHz={device.max_sample_rate_hz||(guided?1:20)} onChange={hz=>setDraft(c=>({...c,sample_rate_hz:hz}))}/></Field><Field label={pt("Uploadinterval (seconden)")}><Input type="number" min="1" value={draft.upload_interval_seconds} onChange={e => setDraft(c => ({

              ...c,

              upload_interval_seconds: Number(e.target.value)

            }))} /></Field>{[['pre_seconds', pt("Voor trigger (s)")], ['post_seconds', pt("Na trigger (s)")], ['hysteresis_bar', pt("Hysterese (bar)")]].map(([k, l]) => <Field key={k} label={l}><Input type="number" min="0" step="any" value={draft.capture[k]} onChange={e => setDraft(c => ({

              ...c,

              capture: {

                ...c.capture,

                [k]: Number(e.target.value)

              }

            }))} /></Field>)}</div><p className="mt-4 text-sm text-muted-foreground">{pt("Alle raw-samples blijven behouden. Een geldige meting boven de werkdruk opent een capture voor alle kanalen. Kortere hardwaremeetintervallen vereisen verificatie.")}</p></div><Button disabled={busy || !draft.channels.length}>{pt("Configuratierevisie opslaan")}</Button></form><Button type="button" size="sm" variant="ghost" className="text-muted-foreground" aria-expanded={manage} onClick={() => setManage(v => !v)}>{pt("Profiel beheren / Nieuw profiel")}</Button>{manage && <SensorLibrary organizationId={device.organization_id} onSaved={() => {

      setProfileTick(v => v + 1);

      setManage(false);

    }} />}</div>;

}

export function VirtualEditor({

  config,

  device,

  onSaved

}) {

  const {

    pt,

    locale

  } = usePressureText();

  const [type, setType] = useState('delta_p'),

    [name, setName] = useState(''),

    [inputs, setInputs] = useState({}),

    [parameters, setParameters] = useState({});

  const spec = FORMULAS[type];

  useEffect(() => {

    setInputs(Object.fromEntries(Object.entries(spec.inputs).map(([k, unit]) => [k, {

      value: 0,

      unit,

      source: ''

    }])));

    setParameters(Object.fromEntries(Object.keys(spec.params).map(k => [k, k === 'd' ? 0 : 100])));

  }, [spec]);

  const channels = [...(config?.channels || []).map(c => ({

    id: c.channel_id,

    name: c.name,

    unit: 'bar'

  })), ...(config?.virtual_channels || []).filter(c => c.enabled).map(c => ({

    id: c.channel_id,

    name: c.name,

    unit: c.output_unit

  }))];

  const save = async list => {

    try {

      const c = await pwrite(`/devices/${device.id}/config`, 'PUT', {

        ...config,

        config_version: config.config_version + 1,

        virtual_channels: list

      });

      onSaved(c);

      toast.success(pt("Virtuele kanalen opgeslagen."));

    } catch (e) {

      toast.error(pressureError(e));

    }

  };

  const add = e => {

    e.preventDefault();

    const id = 'V' + (Math.max(0, ...config.virtual_channels.map(c => Number(c.channel_id.slice(1)))) + 1);

    save([...config.virtual_channels, {

      channel_id: id,

      name,

      enabled: true,

      formula_version: 1,

      formula_type: type,

      inputs,

      parameters,

      output_unit: spec.unit

    }]);

  };

  if (!config) return <p>{pt("Sla eerst de fysieke kanalen op.")}</p>;

  return <div className="space-y-5"><div className={box}><h2 className="text-xl font-bold">{pt("Virtuele kanalen")}</h2><div className="mt-4 grid gap-3 sm:grid-cols-2">{config.virtual_channels.map(v => <div key={v.channel_id} className="rounded-lg border p-4"><b>{v.channel_id} · {v.name}</b><p className="mt-1 text-sm text-muted-foreground">{pt(FORMULAS[v.formula_type]?.name)} · {v.output_unit}</p><p className="mt-2 text-sm">{Object.entries(v.inputs).map(([k, val]) => `${k}: ${typeof val === 'string' ? val : `${val.value} ${val.unit} (${pt("Aangenomen")}: ${val.source})`}`).join(' · ')}</p><Button type="button" size="sm" variant="ghost" onClick={() => save(config.virtual_channels.filter(c => c.channel_id !== v.channel_id))}>{pt("Verwijderen")}</Button></div>)}</div></div><form onSubmit={add} className={box}><h3 className="font-bold">{pt("Berekening toevoegen")}</h3><div className="mt-4 grid gap-4 sm:grid-cols-2"><Field label={pt("Naam")}><Input required value={name} onChange={e => setName(e.target.value)} /></Field><Field label={pt("Berekening")}><select className={selectClass} value={type} onChange={e => setType(e.target.value)}>{Object.entries(FORMULAS).map(([k, v]) => <option key={k} value={k}>{pt(v.name)}</option>)}</select></Field></div>{Object.entries(spec.inputs).map(([key, unit]) => <div className="mt-4 grid gap-3 rounded-lg bg-muted/30 p-4 sm:grid-cols-3" key={key}><Field label={`${key} (${unit})`}><select className={selectClass} value={typeof inputs[key] === 'string' ? inputs[key] : 'constant'} onChange={e => setInputs(v => ({

            ...v,

            [key]: e.target.value === 'constant' ? {

              value: 0,

              unit,

              source: ''

            } : e.target.value

          }))}><option value="constant">{pt("Aangenomen constante")}</option>{channels.filter(c => c.unit === unit).map(c => <option key={c.id} value={c.id}>{c.id} · {c.name}</option>)}</select></Field>{typeof inputs[key] !== 'string' && <><Field label={`${pt("Waarde")} (${unit})`}><Input type="number" step="any" value={inputs[key]?.value ?? 0} onChange={e => setInputs(v => ({

              ...v,

              [key]: {

                ...v[key],

                value: Number(e.target.value)

              }

            }))} /></Field><Field label={pt("Bron van aanname")}><Input required value={inputs[key]?.source || ''} onChange={e => setInputs(v => ({

              ...v,

              [key]: {

                ...v[key],

                source: e.target.value

              }

            }))} /></Field></>}</div>)}<div className="mt-4 grid gap-4 sm:grid-cols-2">{Object.entries(spec.params).map(([k, l]) => <Field key={k} label={l}><Input type="number" step="any" required value={parameters[k] ?? 0} onChange={e => setParameters(p => ({

            ...p,

            [k]: Number(e.target.value)

          }))} /></Field>)}</div><p className="my-4 text-sm text-muted-foreground">{pt("Ideale waarden; geen wrijvingscorrectie. Alleen gelijktijdige geldige inputs worden gecombineerd.")}</p><Button>{pt("Berekening bewaren")}</Button></form></div>;

}

